// Dev-only mock seed — used when VITE_USE_MOCKS=true so the UI can be worked
// on without a running platform. Shapes match what /runs returns (ApiRun).
//
// Production must keep this off (default). Switch via env var, not by editing.

import type { ApiRun } from './types';

const now = new Date();
const iso = (mins: number): string => new Date(now.getTime() - mins * 60_000).toISOString();

export const MOCK_RUNS: ApiRun[] = [
  // BACKLOG
  {
    id: 'mock-2187',
    ticket_id: 'ENG-2187',
    jira_issue_key: null,
    pr_number: null,
    branch: null,
    strategy: 'claude-managed',
    status: 'queued',
    audit_verdict: null,
    error: null,
    started_at: iso(120),
    finished_at: null,
    cost_usd: '0.00',
    num_turns: null,
    github_owner: null,
    github_repo: null,
    metadata: {
      title: 'Add password reset flow with magic link expiry + rate limiting',
      source: 'chat',
      execution_mode: 'autonomous',
      agent_provider: 'anthropic',
      review_provider: 'google',
      review_loops: 3,
      description: 'Users currently have no way to reset their password if they forget it. Build a flow that issues a one-time magic link via email, expires after 15 minutes, and is rate-limited to 3 requests per hour per email.',
      acceptance_criteria: [
        { text: 'POST /auth/reset accepts an email, issues link, returns 202', done: false },
        { text: 'Link contains signed JWT with 15min TTL', done: false },
        { text: 'Rate limit: 3/hour per email, 10/hour per IP', done: false },
        { text: 'Email template uses existing transactional pipeline', done: false },
      ],
    },
  },
  // IMPLEMENTING
  {
    id: 'mock-1934',
    ticket_id: 'ENG-1934',
    jira_issue_key: null,
    pr_number: null,
    branch: 'agent/eng-1934',
    strategy: 'claude-agent-sdk',
    status: 'running',
    audit_verdict: null,
    error: null,
    started_at: iso(8),
    finished_at: null,
    cost_usd: '0.42',
    num_turns: 14,
    github_owner: null,
    github_repo: null,
    metadata: {
      title: 'Implement /users/:id/profile GET endpoint with avatar resolver',
      source: 'chat',
      execution_mode: 'assisted',
      agent_provider: 'anthropic',
      review_provider: 'google',
      review_loops: 3,
      zone: 'green',
      progress: 0.34,
      phase: 'implementing',
    },
  },
  // REVIEW
  {
    id: 'mock-1854',
    ticket_id: 'ENG-1854',
    jira_issue_key: 'ENG-1854',
    pr_number: 8410,
    branch: 'agent/eng-1854',
    strategy: 'claude-managed',
    status: 'running',
    audit_verdict: null,
    error: null,
    started_at: iso(45),
    finished_at: null,
    cost_usd: '2.18',
    num_turns: 32,
    github_owner: null,
    github_repo: null,
    metadata: {
      title: 'Add idempotency keys to checkout-session mutations',
      source: 'jira',
      execution_mode: 'autonomous',
      agent_provider: 'anthropic',
      review_provider: 'openai',
      review_loops: 3,
      zone: 'red',
      progress: 0.4,
      phase: 'review',
      loop: { current: 1, max: 3, issues: 4 },
      sandbox_id: 'sb_iad_3c91ae',
      sandbox_region: 'iad',
    },
  },
  // PR
  {
    id: 'mock-1721',
    ticket_id: 'ENG-1721',
    jira_issue_key: 'ENG-1721',
    pr_number: 8421,
    branch: 'agent/eng-1721',
    strategy: 'claude-managed',
    status: 'succeeded',
    audit_verdict: 'pass',
    error: null,
    started_at: iso(80),
    finished_at: iso(20),
    cost_usd: '4.12',
    num_turns: 41,
    github_owner: null,
    github_repo: null,
    metadata: {
      title: 'Add SSO claim-mapping for Okta groups → workspace roles',
      source: 'jira',
      execution_mode: 'autonomous',
      agent_provider: 'anthropic',
      review_provider: 'google',
      zone: 'green',
      pr_additions: 87,
      pr_deletions: 12,
      pr_label: 'human-ready',
    },
  },
  // DONE — merged
  {
    id: 'mock-1487',
    ticket_id: 'ENG-1487',
    jira_issue_key: null,
    pr_number: 8201,
    branch: 'agent/eng-1487',
    strategy: 'claude-managed',
    status: 'succeeded',
    audit_verdict: 'pass',
    error: null,
    started_at: iso(2880),
    finished_at: iso(2820),
    cost_usd: '0.78',
    num_turns: 22,
    github_owner: null,
    github_repo: null,
    metadata: {
      title: 'Add /healthz with dependency probes',
      source: 'manual',
      execution_mode: 'autonomous',
      agent_provider: 'anthropic',
      review_provider: 'google',
      zone: 'green',
      pr_additions: 124,
      pr_deletions: 8,
      pr_label: 'merged',
      merged: true,
    },
  },
  // DONE — escalated
  {
    id: 'mock-1187',
    ticket_id: 'ENG-1187',
    jira_issue_key: 'ENG-1187',
    pr_number: null,
    branch: null,
    strategy: 'claude-managed',
    status: 'escalated',
    audit_verdict: 'block',
    error: 'scope ambiguity around partial refunds',
    started_at: iso(4320),
    finished_at: iso(4200),
    cost_usd: '8.12',
    num_turns: 90,
    github_owner: null,
    github_repo: null,
    metadata: {
      title: 'Fix Stripe webhook idempotency',
      source: 'jira',
      execution_mode: 'autonomous',
      agent_provider: 'openai',
      review_provider: 'anthropic',
      zone: 'red',
    },
  },
];

export const MOCK_WIP = {
  scopes: [
    { scope: 'agent_provider:anthropic', in_flight: 2, cap: 5, updated_at: now.toISOString() },
    { scope: 'agent_provider:openai', in_flight: 1, cap: 5, updated_at: now.toISOString() },
    { scope: 'global', in_flight: 3, cap: 10, updated_at: now.toISOString() },
  ],
};
